import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Fragment } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GET_STARTED_LINKS, navLinks } from "../../constants";
import { buildOctantProductPath, OCTANT_NAV_LINKS } from "../../services/octant/octantPageService";
import { MdaiLink } from "../MdaiLink";

const octantEnabled = import.meta.env.VITE_OCTANT_ENABLED === "true";

interface MobileNavLinksProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  handleClose?: () => void;
}

export function MobileNavLinks({
  isOpen,
  onOpenChange,
  handleClose,
}: MobileNavLinksProps) {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const headerGroups = navLinks.filter((g) => g.header);
  const exploreGroup = navLinks.find((g) => g.groupName === "Explore Octant");

  return (
    <Box>
      <Stack
        sx={{
          alignItems: "center",
          display: { sm: "flex", md: "none" },
          flexDirection: "row",
        }}
      >
        <IconButton
          size="small"
          id="menu-button"
          aria-label="navigation"
          aria-controls={isOpen ? "nav-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? "true" : undefined}
          onClick={() => onOpenChange?.(!isOpen)}
          role="navigation"
          sx={{ display: { md: "none" } }}
        >
          {isOpen ? (
            <Close sx={{ color: "var(--mdai-light-grey)" }} />
          ) : (
            <MenuIcon sx={{ color: "var(--mdai-light-grey)" }} />
          )}
        </IconButton>
      </Stack>
      <Drawer
        anchor={isSmUp ? "right" : "top"}
        variant="persistent"
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          root: { sx: { top: "76px" } },
          paper: {
            sx: {
              top: "76px",
              padding: "8px 20px 32px 20px",
              ...(isSmUp && { width: 360 }),
            },
          },
          backdrop: { sx: { top: "76px" } },
        }}
      >
        <Stack sx={{ fontWeight: 500 }}>
          {octantEnabled && (
            <Fragment key="Octant">
              <Accordion sx={{ boxShadow: "none" }} defaultExpanded>
                <AccordionSummary
                  sx={{
                    p: 0,
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "Geist, Helvetica, Arial, sans-serif",
                    flexDirection: "row-reverse",
                  }}
                  expandIcon={<ExpandMore sx={{ color: "var(--mdai-light-grey)" }} />}
                >
                  <span style={{ marginLeft: "4px" }}>Product</span>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List>
                    {OCTANT_NAV_LINKS.map(({ label, href }) => (
                      <ListItem key={label}>
                        <MdaiLink label={label} href={href} onClick={handleClose} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
              <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.12)" }} />
            </Fragment>
          )}
          {headerGroups.map(({ groupName, links }) => (
            <Fragment key={groupName}>
              <Accordion
                sx={{ boxShadow: "none" }}
                defaultExpanded={groupName === "Platform"}
              >
                <AccordionSummary
                  sx={{
                    p: 0,
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: "Geist, Helvetica, Arial, sans-serif",
                    flexDirection: "row-reverse",
                  }}
                  expandIcon={
                    <ExpandMore sx={{ color: "var(--mdai-light-grey)" }} />
                  }
                >
                  <span style={{ marginLeft: "4px" }}>{groupName}</span>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List>
                    {links.map(({ label, href }) => (
                      <ListItem key={label}>
                        <MdaiLink
                          label={label}
                          href={href}
                          onClick={handleClose}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
              <Divider
                sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.12)" }}
              />
              {groupName === "Platform" && octantEnabled && (
                <>
                  <MdaiLink
                    type="button"
                    variant="text"
                    style={{
                      color: "#fff !important",
                      padding: "0 !important",
                      fontSize: "16px !important",
                      margin: "8px 0",
                    }}
                    label={"Open Source"}
                    href={buildOctantProductPath("open-source")}
                    onClick={handleClose}
                  />
                  <Divider
                    sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.12)" }}
                  />
                </>
              )}
            </Fragment>
          ))}
          <Fragment key="Get Started">
            <Accordion sx={{ boxShadow: "none" }}>
              <AccordionSummary
                sx={{
                  p: 0,
                  fontSize: "16px",
                  fontWeight: 500,
                  fontFamily: "Geist, Helvetica, Arial, sans-serif",
                  flexDirection: "row-reverse",
                }}
                expandIcon={<ExpandMore sx={{ color: "var(--mdai-light-grey)" }} />}
              >
                <span style={{ marginLeft: "4px" }}>Get Started</span>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List>
                  {GET_STARTED_LINKS.map(({ label, href }) => (
                    <ListItem key={label}>
                      <MdaiLink label={label} href={href} onClick={handleClose} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
            <Divider sx={{ borderBottom: "1px solid rgba(255, 255, 255, 0.12)" }} />
          </Fragment>
        </Stack>
        <Stack gap={3}>
          <Stack
            alignItems={"flex-start"}
            sx={{
              padding: 2,
              margin: 1,
              borderRadius: "4px",
              border: "1px solid rgba(213, 0, 249, 0.50)",
              background: "rgba(213, 0, 249, 0.16)",
            }}
          >
            <Typography sx={{ fontSize: "16px !important", fontWeight: 500 }}>
              {exploreGroup?.groupName ?? "Explore Octant"}
            </Typography>
            <List sx={{ pt: 2 }}>
              {exploreGroup?.links.map(({ label, href }) => (
                  <ListItem
                    key={label}
                    sx={{
                      pl: 0,
                      borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                      "&:last-of-type": {
                        borderBottom: "none",
                      },
                    }}
                  >
                    <MdaiLink label={label} href={href} onClick={handleClose} />
                  </ListItem>
                ))}
            </List>
          </Stack>

          <Button
            variant="contained"
            href={"#schedule-call"}
            sx={{ width: "100%" }}
            onClick={handleClose}
          >
            Contact Sales
          </Button>
        </Stack>
      </Drawer>
    </Box>
  );
}
